import {
  Button,
  Card, Grid, IconButton, TextField, Tooltip,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';
import RequiredFiled from '../../../../../Common/RequiredField';
import searchIcon from '../../../../../assets/search_icon.svg';
import ComponentTextInput from '../../../../../Common/ComponentTextinput/ComponentTextInput';
import { MainDefaultValue } from '../utils/constants';
import {
  EnumAddFreonForm, FreonMainFormValues, PayloadAddFreonCylinderTypes,
} from '../utils/types';
import AddCustomerModal from '../../../../../Common/AddCustomerModal';
import { GetCustomerByIdPayload } from '../../../../addCustomer/utils/types';
import { getCustomerByIdStart, removeSelectedCustomer } from '../../../../addCustomer/redux/addCustomerSlice';
import { RootState } from '../../../../../redux/rootState';
import {
  createCylinder, editFreonStart, resetStateFreon, unitPrice,
} from '../redux/sliceAddFreonCylinder';
import EndPoints from '../../../../../Routes/EndPoints';
import { LocalizationContext } from '../../../../../Common/Localization/LocalizationProvider';
import { getFreonByIdStart, resetData } from '../../FreonCylinderList/redux/sliceListFreonCylinder';
import { MainFormSchema } from '../utils/validation';
import { setLoader } from '../../../../../redux/loaderSlice';
import { CommonDateInput } from '../../../../../Common/CommonDateInput';
import { Regex18With2D, RegexFiveWith2D } from '../../../../../utils/regex';
import { stripCustPrefix } from '../../../../../utils/CommonFunctions';

export function ComponentAddFreonCylinder() {
  const [price, setPrice] = useState(0);
  const [totalKits, setTotalKits] = useState<number>(0);
  const [additionalAmount, setAdditionalAmount] = useState(0);
  const navigate = useNavigate();
  const { kitId } = useParams();
  const [kidId, setKitId] = useState('');
  const { getKitDetailsById, isLoading } = useSelector((state: RootState) => state.FreonListReducer);
  const { translations } = useContext(LocalizationContext);

  const [showModal, setShowModal] = useState(false);

  const dispatch = useDispatch();
  const { otherCustomerdetails } = useSelector((state: RootState) => state.addCustomerReducer);
  const {
    unitPriceInitial, isSuccess,
  } = useSelector((state: RootState) => state.createFreonReducer);

  const {
    handleSubmit, control,
    formState: { errors },
    setValue, reset, trigger,
  } = useForm<FreonMainFormValues>({
    defaultValues: MainDefaultValue,
    resolver: yupResolver(MainFormSchema as unknown as ObjectSchema<FreonMainFormValues>),
  });

  useEffect(() => {
    if (getKitDetailsById?.CustId) {
      [
        { name: EnumAddFreonForm.customerSearch, value: stripCustPrefix(getKitDetailsById.CustId) },
        { name: EnumAddFreonForm.customer, value: (getKitDetailsById.CustomerName) },
        { name: EnumAddFreonForm.contact, value: getKitDetailsById.ContactNumber },
        { name: EnumAddFreonForm.shipVia, value: getKitDetailsById.ShipVia },
        { name: EnumAddFreonForm.shipAddress, value: getKitDetailsById?.ShippingAddress },
        { name: EnumAddFreonForm.customerAddress, value: getKitDetailsById?.CustomerAddress },
        { name: EnumAddFreonForm.customerAddressOptional, value: getKitDetailsById?.CustomerAddressOptional },
        { name: EnumAddFreonForm.customerCountry, value: getKitDetailsById?.CustomerCountry },
        { name: EnumAddFreonForm.customerZip, value: getKitDetailsById?.CustomerZip },
        { name: EnumAddFreonForm.customerState, value: getKitDetailsById?.CustomerState },
        { name: EnumAddFreonForm.customerCity, value: getKitDetailsById?.CustomerCity },
        { name: EnumAddFreonForm.blueCylinders, value: getKitDetailsById.NumberOfKits },
        { name: EnumAddFreonForm.cylindersPrice, value: getKitDetailsById.PerUnitPrice },
        { name: EnumAddFreonForm.additionalAmount, value: getKitDetailsById.AdditionalAmount },
        { name: EnumAddFreonForm.billAmount, value: getKitDetailsById.TotalAmount },
        { name: EnumAddFreonForm.invoice, value: getKitDetailsById.InvoiceNumber },
        { name: EnumAddFreonForm.PO, value: getKitDetailsById.PurchaseOrderNumber },
        { name: EnumAddFreonForm.shipDate, value: getKitDetailsById?.ShipDate?.length > 0 ? dayjs(new Date(getKitDetailsById?.ShipDate)) : null },
        {
          name: EnumAddFreonForm.invoiceDate,
          value: getKitDetailsById?.InvoiceDate?.length > 0 ? dayjs(new Date(getKitDetailsById?.InvoiceDate)) : null,
        },
      ].forEach(({ name, value }) => setValue(name, value));
      setKitId(getKitDetailsById?.KitId);
      setTotalKits(getKitDetailsById?.NumberOfKits);
      setAdditionalAmount(getKitDetailsById?.AdditionalAmount);
    } else {
      [
        { name: EnumAddFreonForm.invoiceDate, value: null },
      ].forEach(({ name, value }) => setValue(name, value));
    }
  }, [getKitDetailsById]);

  const handleTabCell = (customerId: string) => {
    setShowModal(false);
    const payload: GetCustomerByIdPayload = {
      customerId,
      otherCustomer: true,
    };
    dispatch(getCustomerByIdStart(payload));
    setValue(EnumAddFreonForm.customerSearch, stripCustPrefix(customerId));
    trigger(EnumAddFreonForm.customerSearch);
  };

  const onSubmit = (values: FreonMainFormValues) => {
    const formattedShipDate = (dayjs(values?.shipDate).format('DD-MM-YYYY') || '');
    const formattedInvoiceDate = values?.invoiceDate ? (dayjs(values?.invoiceDate).format('DD-MM-YYYY') || '') : null;

    const data: PayloadAddFreonCylinderTypes = {
      type: 1,
      CustId: `Cust-${values.customerSearch}`,
      PurchaseOrderNumber: values.PO,
      ContactNumber: values.contact,
      ShipDate: formattedShipDate,
      ShipVia: values.shipVia,
      ShippingAddress: values.shipAddress,
      NumberOfKits: Number(values.blueCylinders),
      PerUnitPrice: Number(values.cylindersPrice),
      AdditionalAmount: Number(values.additionalAmount),
      TotalAmount: Number(values.billAmount),
      InVoiceNumber: values.invoice,
      InvoiceDate: formattedInvoiceDate,
    };
    if (kitId) {
      const reqBody = {
        ...data,
        KitId: kidId,
      };
      dispatch(editFreonStart(reqBody));
    } else {
      dispatch(createCylinder(data));
    }
  };

  useEffect(() => {
    if (otherCustomerdetails) {
      setValue(EnumAddFreonForm.customer, otherCustomerdetails?.CompanyInformation?.CustomerName);
      setValue(EnumAddFreonForm.customerAddress, otherCustomerdetails?.CompanyInformation?.Address);
      setValue(EnumAddFreonForm.customerAddressOptional, otherCustomerdetails?.CompanyInformation?.OptionalAddress || '');
      setValue(EnumAddFreonForm.customerCountry, otherCustomerdetails?.CompanyInformation?.CountryCode || '');
      setValue(EnumAddFreonForm.customerZip, otherCustomerdetails?.CompanyInformation?.ZipCode || '');
      setValue(EnumAddFreonForm.customerState, otherCustomerdetails?.CompanyInformation?.StateName || '');
      setValue(EnumAddFreonForm.customerCity, otherCustomerdetails?.CompanyInformation?.CityName || '');
    }
  }, [otherCustomerdetails]);

  useEffect(() => {
    if (getKitDetailsById?.CustId) {
      setValue(EnumAddFreonForm.cylindersPrice, getKitDetailsById?.PerUnitPrice);
      setPrice(getKitDetailsById?.PerUnitPrice);
    } else if (unitPriceInitial >= 0) {
      setValue(EnumAddFreonForm.cylindersPrice, unitPriceInitial);
      setPrice(unitPriceInitial);
    }
  }, [unitPriceInitial, getKitDetailsById]);

  useEffect(() => {
    const val = (Number(totalKits) * Number(price)) + Number(additionalAmount);
    setValue(EnumAddFreonForm.billAmount, val);
  }, [totalKits, price, additionalAmount]);

  useEffect(() => {
    dispatch(unitPrice());
    return () => {
      dispatch(resetData());
      dispatch(removeSelectedCustomer());
    };
  }, []);

  const handleCancel = () => {
    dispatch(removeSelectedCustomer());
    dispatch(resetStateFreon());
    navigate(EndPoints.FREON_CYLINDER);
  };

  useEffect(() => {
    if (kitId) {
      const data = {
        KitId: kitId,
      };
      dispatch(getFreonByIdStart(data));
    }
  }, [kitId]);

  useEffect(() => {
    if (isSuccess) {
      navigate(EndPoints.FREON_CYLINDER);
      dispatch(resetStateFreon());
      dispatch(removeSelectedCustomer());
      reset();
    }
  }, [isSuccess]);

  useEffect(() => {
    dispatch(setLoader(isLoading));
  }, [isLoading]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="div_job_container_info customBodyWithoutBtnContainer">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Card>
                <div className="customerInfo_container">
                  <div className="customerInfo">
                    <Controller
                      name={EnumAddFreonForm.customerSearch}
                      control={control}
                      render={({ field: { value } }) => (
                        <TextField
                          size="small"
                          placeholder="Customer ID *"
                          value={value}
                          onClick={() => setShowModal(true)}
                          error={!!errors.customerSearch}
                          helperText={errors?.customerSearch?.message}
                          InputProps={{
                            endAdornment: (
                              <Tooltip title="Search here..">
                                <IconButton onClick={() => setShowModal(true)}>
                                  <img src={searchIcon} alt="" />
                                </IconButton>
                              </Tooltip>
                            ),
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="customerInfo_main_container">
                  <Grid container spacing={2}>
                    <Grid item md={4} sm={6} xs={12}>
                      <div className="div_label_text">
                        <label className="label_Style_job">{translations.customer}</label>
                        <ComponentTextInput
                          control={control}
                          isDisable
                          name={EnumAddFreonForm.customer}
                          size="small"
                          id={EnumAddFreonForm.customer}
                          errors={errors}
                        />
                      </div>
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                      <div className="div_label_text">
                        <label className="label_Style_job">{translations.contact}</label>
                        <ComponentTextInput
                          control={control}
                          regEx={/^[A-Za-z ]+$/}
                          inputProps={{ maxLength: 16 }}
                          name={EnumAddFreonForm.contact}
                          size="small"
                          id={EnumAddFreonForm.contact}
                          errors={errors}
                        />
                      </div>
                    </Grid>
                    <Grid item md={2} sm={6} xs={12}>
                      <div className="div_label_text">
                        <label className="label_Style_job">{translations.shipDate}</label>
                        <div className="datePicker_style">
                          {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              value={shipDate}
                              onChange={(newValue) => dispatch(setShipDate(newValue))}
                              slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                          </LocalizationProvider> */}
                          <CommonDateInput
                            control={control}
                            errors={errors}
                            name={EnumAddFreonForm.shipDate}
                          />
                        </div>
                      </div>
                    </Grid>
                    <Grid item md={2} sm={12} xs={12}>
                      <div className="div_label_text">
                        <label className="label_Style_job">{translations.shipVia}</label>
                        <ComponentTextInput
                          control={control}
                          regEx={/^[A-Za-z ]+$/}
                          inputProps={{ maxLength: 20 }}
                          name={EnumAddFreonForm.shipVia}
                          size="small"
                          id={EnumAddFreonForm.shipVia}
                          errors={errors}
                        />
                      </div>
                    </Grid>
                    <Grid item md={4} sm={12} xs={12} lg={3} xl={2}>
                      <div className="div_label_text">
                        <label className="label_Style_job">
                          {translations.blueCylinders}
                          {' '}
                          <RequiredFiled />
                        </label>
                        <ComponentTextInput
                          control={control}
                          regEx={/^-?\d+$/}
                          inputProps={{ maxLength: 3 }}
                          name={EnumAddFreonForm.blueCylinders}
                          size="small"
                          id={EnumAddFreonForm.blueCylinders}
                          errors={errors}
                          handleChange={(name, text) => setTotalKits(Number(text))}
                        />
                      </div>
                    </Grid>
                    <Grid item md={4} sm={12} xs={12} lg={3} xl={2}>
                      <div className="div_label_text">
                        <label className="label_Style_job">
                          {translations.cylindersPriceEach}
                          {' '}
                          ($)
                        </label>
                        <ComponentTextInput
                          control={control}
                          regEx={RegexFiveWith2D}
                          // inputProps={{ maxLength: 5 }}
                          name={EnumAddFreonForm.cylindersPrice}
                          size="small"
                          id={EnumAddFreonForm.cylindersPrice}
                          errors={errors}
                          handleChange={(name, text) => setPrice(Number(text))}
                        />
                      </div>
                    </Grid>
                    <Grid item md={4} sm={12} xs={12} lg={3} xl={4}>
                      <div className="div_label_text">
                        <label className="label_Style_job">
                          Shipping Address
                        </label>
                        <ComponentTextInput
                          control={control}
                          // regEx={RegexFiveWith2D}
                          inputProps={{ maxLength: 200 }}
                          name={EnumAddFreonForm.shipAddress}
                          size="small"
                          id={EnumAddFreonForm.shipAddress}
                          errors={errors}
                        />
                      </div>
                    </Grid>
                    <Grid item md={12} sm={12} xs={12} lg={6} xl={6}>
                      <div className="div_label_text">
                        <label className="label_Style_disabled">
                          Customer Address
                        </label>
                        <ComponentTextInput
                          control={control}
                          isDisable
                          // inputProps={{ maxLength: 200 }}
                          name={EnumAddFreonForm.customerAddress}
                          size="small"
                          id={EnumAddFreonForm.customerAddress}
                          errors={errors}
                        />
                      </div>
                    </Grid>
                    <Grid item md={12} sm={12} xs={12} lg={6} xl={6}>
                      <div className="div_label_text">
                        <label className="label_Style_disabled">
                          Customer Address(Optional)
                        </label>
                        <ComponentTextInput
                          control={control}
                          isDisable
                          // inputProps={{ maxLength: 200 }}
                          name={EnumAddFreonForm.customerAddressOptional}
                          size="small"
                          id={EnumAddFreonForm.customerAddressOptional}
                          errors={errors}
                        />
                      </div>
                    </Grid>
                    <Grid item md={4} sm={12} xs={12} lg={3} xl={2}>
                      <div className="div_label_text">
                        <label className="label_Style_job">
                          Country
                        </label>
                        <ComponentTextInput
                          control={control}
                          name={EnumAddFreonForm.customerCountry}
                          size="small"
                          id={EnumAddFreonForm.customerCountry}
                          isDisable
                        />
                      </div>
                    </Grid>
                    <Grid item md={4} sm={12} xs={12} lg={3} xl={2}>
                      <div className="div_label_text">
                        <label className="label_Style_job">
                          Zip
                        </label>
                        <ComponentTextInput
                          control={control}
                          name={EnumAddFreonForm.customerZip}
                          size="small"
                          id={EnumAddFreonForm.customerZip}
                          isDisable
                        />
                      </div>
                    </Grid>
                    <Grid item md={4} sm={12} xs={12} lg={3} xl={2}>
                      <div className="div_label_text">
                        <label className="label_Style_job">
                          State
                        </label>
                        <ComponentTextInput
                          control={control}
                          name={EnumAddFreonForm.customerState}
                          size="small"
                          id={EnumAddFreonForm.customerState}
                          isDisable
                        />
                      </div>
                    </Grid>
                    <Grid item md={4} sm={12} xs={12} lg={3} xl={3}>
                      <div className="div_label_text">
                        <label className="label_Style_job">
                          City
                        </label>
                        <ComponentTextInput
                          control={control}
                          name={EnumAddFreonForm.customerCity}
                          size="small"
                          id={EnumAddFreonForm.customerCity}
                          isDisable
                        />
                      </div>
                    </Grid>
                  </Grid>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Card>
                <div className="customerInfo_container">
                  <div className="customerInfo">
                    {translations.billing}
                  </div>
                </div>
                <div className="customerInfo_main_container">
                  <Grid container spacing={2}>
                    <Grid item md={3} sm={6} xs={12}>
                      <div className="div_label_text">
                        <label className="label_Style_job">
                          {translations.additionalAmount}
                          {' '}
                          ($)
                        </label>
                        <ComponentTextInput
                          control={control}
                          regEx={Regex18With2D}
                          // inputProps={{ maxLength: 18 }}
                          name={EnumAddFreonForm.additionalAmount}
                          size="small"
                          id={EnumAddFreonForm.additionalAmount}
                          errors={errors}
                          handleChange={(name, text) => setAdditionalAmount(Number(text))}
                        />
                      </div>
                    </Grid>
                    <Grid item md={3} sm={6} xs={12}>
                      <div className="div_label_text">
                        <label className="label_Style_job">
                          {translations.billAmount}
                          {' '}
                          ($)
                        </label>
                        <ComponentTextInput
                          control={control}
                          isDisable
                          regEx={/^-?\d+$/}
                          name={EnumAddFreonForm.billAmount}
                          size="small"
                          id={EnumAddFreonForm.billAmount}
                        />
                      </div>
                    </Grid>
                    <Grid item md={3} sm={12} xs={12}>
                      <div className="div_label_text">
                        <label className="label_Style_job">{translations.invoice}</label>
                        <ComponentTextInput
                          control={control}
                          inputProps={{ maxLength: 20 }}
                          name={EnumAddFreonForm.invoice}
                          size="small"
                          id={EnumAddFreonForm.invoice}
                          errors={errors}
                        />
                      </div>
                    </Grid>
                    <Grid item md={3} sm={6} xs={12}>
                      <div className="div_label_text">
                        <label className="label_Style_job">{translations.invoiceDate}</label>
                        <div className="datePicker_style">
                          <CommonDateInput
                            control={control}
                            errors={errors}
                            name={EnumAddFreonForm.invoiceDate}
                          />
                        </div>
                      </div>
                    </Grid>
                    <Grid item md={3} sm={12} xs={12}>
                      <div className="div_label_text">
                        <label className="label_Style_job">{translations.PO}</label>
                        <ComponentTextInput
                          control={control}
                          inputProps={{ maxLength: 25 }}
                          name={EnumAddFreonForm.PO}
                          size="small"
                          id={EnumAddFreonForm.PO}
                          errors={errors}
                        />
                      </div>
                    </Grid>
                  </Grid>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <div className="save_and_next_div">
                <div>
                  <Button className="button_cancel" onClick={handleCancel}>{translations.cancel}</Button>
                </div>
                <div className="button_margin_left">
                  <Button type="submit" className="button_save_and_next">{translations.save}</Button>
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      </form>
      {showModal && (
        <AddCustomerModal show={showModal} setOpenFrom={() => setShowModal(false)} handleTabCell={handleTabCell} />
      )}
    </>
  );
}
